// Copyright 2025 Sorama B.V.
import {
    AnalysisWorkflowModuleCatalog,
    IAnalysisWorkflowModuleConfig,
} from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { IWorkflowRouter } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { AnalysisOverviewModule } from "./AnalysisOverview/AnalysisOverviewModule";

export interface IMeasurementWorkflowModule {
    selectedMeasurementIds: string[];
}

export class AnalysisWorkflowModuleHelpers {
    /**
     * Get the instance of the overview module from the workflow
     * @param workflow the analysis workflow
     * @returns the instance of the overview module
     */
    private static getOverViewModuleInstance(workflow: IWorkflowRouter) {
        const openModules = workflow.getModules();
        const overviewModule = openModules.find(
            (m) => m.config.name === AnalysisWorkflowModuleCatalog.AnalysisOverviewModule.name
        );
        return overviewModule.moduleInstance.moduleReference as unknown as AnalysisOverviewModule;
    }

    /**
     * Opens a new analysis module for the given measurement
     * @param workflow the analysis workflow (this.moduleInstance.getWorkflow())
     * @param measurementId the id of the measurement to open the analysis module for
     * @param analysisModule the module to open
     */
    public static async openModuleForMeasurement(
        workflow: IWorkflowRouter,
        measurementId: string,
        analysisModule: IAnalysisWorkflowModuleConfig
    ): Promise<void> {
        const overViewModuleInstance = this.getOverViewModuleInstance(workflow);
        const newAnalysisModule = await workflow.addModule(analysisModule);
        overViewModuleInstance.selectedMeasurementIds.add(newAnalysisModule.moduleId, [measurementId]);
    }

    /**
     * Checks if the module is already open for the given measurement
     * @param workflow the analysis workflow (this.moduleInstance.getWorkflow())
     * @param measurementId the id of the measurement to check if it is open for
     * @param analysisModule the module type to check if it is open for the measurement
     * @returns true if the module is already open for the measurement, false otherwise
     */
    public static isModuleOpenForMeasurement(
        workflow: IWorkflowRouter,
        measurementId: string,
        analysisModule: IAnalysisWorkflowModuleConfig
    ): boolean {
        return workflow
            .getModules()
            .some(
                (m) =>
                    m.config.moduleType === analysisModule.moduleType &&
                    (
                        m.moduleInstance.moduleReference as unknown as IMeasurementWorkflowModule
                    ).selectedMeasurementIds?.includes(measurementId)
            );
    }

    /**
     * If a new analysis module for the given measurement is not open -> open it
     * @param workflow the analysis workflow (this.moduleInstance.getWorkflow())
     * @param measurementId the id of the measurement to open the analysis module for
     * @param analysisModule the module to open
     */
    public static async ensureModuleOpenForMeasurement(
        workflow: IWorkflowRouter,
        measurementId: string,
        analysisModule: IAnalysisWorkflowModuleConfig
    ): Promise<void> {
        if (!this.isModuleOpenForMeasurement(workflow, measurementId, analysisModule)) {
            await this.openModuleForMeasurement(workflow, measurementId, analysisModule);
        }
    }
}
